/** Pure helpers of the item form and details: rental factor, warranty warning, "where now" (owner remarks 3). */

import { parseScaled } from '../ui/numbers';

/** A line of "Gdzie teraz" as the API sends it: pieces in another location or rented out. */
export type Placement = { place: 'location' | 'rented'; locationId: string | null; rentedTo: string; rentedUntil: string | null; quantity: number };

/** "2,5" / "3 %" / "0.25" -> hundredths of a percent (250 / 300 / 25). 0–100 %, at most two decimals. */
export function parsePercent(text: string): number {
  const bp = parseScaled(text.replace(/%/g, ''), 2, { maxIntegerDigits: 3 });
  if (!(bp !== null && bp >= 0 && bp <= 10_000)) throw new Error('Podaj procent od 0 do 100, np. 2,5');
  return bp;
}

/** 250 -> "2,5": the value put into the form field. */
export function bpToInput(bp: number): string {
  const whole = Math.floor(bp / 100);
  const frac = String(bp % 100).padStart(2, '0').replace(/0+$/, '');
  return frac ? `${whole},${frac}` : String(whole);
}

/** Suggested rental per day: purchase price × factor, in whole grosze (half up); null when either is missing. */
export function suggestedRental(purchaseGrosze: number | null | undefined, factorBp: number | null | undefined): number | null {
  if (purchaseGrosze == null || factorBp == null) return null;
  return Math.round((purchaseGrosze * factorBp) / 10_000);
}

const dayNumber = (day: string) => Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10))) / 86_400_000;

/** The small warning next to "Gwarancja do": passed, or ends within 30 days (`today` in Warsaw, `YYYY-MM-DD`). */
export function warrantyState(until: string | null | undefined, today: string): { kind: 'expired' | 'soon'; label: string } | null {
  if (!until) return null;
  const days = dayNumber(until) - dayNumber(today);
  if (days < 0) return { kind: 'expired', label: 'gwarancja minęła' };
  if (days > 30) return null;
  const when = days === 0 ? 'dziś' : days === 1 ? 'jutro' : `za ${days} dni`;
  return { kind: 'soon', label: `gwarancja kończy się ${when}` };
}

/** Pieces in the default location: what is not elsewhere (never below zero, even when the stock was used up later). */
export function atDefault(total: number, placements: readonly Placement[]): number {
  return Math.max(0, total - placements.reduce((sum, l) => sum + l.quantity, 0));
}

/** "2 wypoż., 1 szt.: Bus": the short "where now" of a list row; empty when everything is in the default location. */
export function whereShort(placements: readonly Placement[], locationNames: ReadonlyMap<string, string>): string {
  const rented = placements.filter((l) => l.place === 'rented').reduce((sum, l) => sum + l.quantity, 0);
  const away = placements.filter((l) => l.place === 'location');
  const awayCount = away.reduce((sum, l) => sum + l.quantity, 0);
  const parts: string[] = [];
  if (rented > 0) parts.push(`${rented} wypoż.`);
  if (awayCount > 0) {
    const places = new Set(away.map((l) => l.locationId));
    const only = places.size === 1 ? locationNames.get(away[0]!.locationId ?? '') : undefined;
    parts.push(only ? `${awayCount} szt.: ${only}` : `${awayCount} szt. poza domyślną`);
  }
  return parts.join(', ');
}

/** "do 2.11.2026" from "2026-11-02". */
export const dayLabel = (day: string): string => `${Number(day.slice(8, 10))}.${day.slice(5, 7)}.${day.slice(0, 4)}`;
